import { useQueryClient } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import React from 'react';
import { AuthContext } from '../contexts/auth-context';
import { signIn } from '../utils/authenticate';
import { captureError } from '../utils/capture-error';

/**
 * Sign out and sign in again, shared by the user menu, the session-expired
 * dialog and the System Screens.
 */
export function useSessionActions() {
  // Context
  const { state, dispatch } = React.useContext(AuthContext);
  // Hooks
  const queryClient = useQueryClient();

  /**
   * Sign out of Cognito, then clear cached data. The shell shows "Signing
   * you out…" from the start, so the page doesn't refetch and flash back
   * while Cognito finishes. Events is public, so a sign out that fails
   * after the session already expired still drops to the signed-out site.
   */
  const signOut = React.useCallback(async () => {
    dispatch({ type: 'SIGNING_OUT', payload: true });
    try {
      await Auth.signOut();
    } catch (error) {
      captureError({ data: { error } });
      if (state.tokenError) {
        // The session is already gone: carry on as a signed-out visitor
        queryClient.removeQueries();
        dispatch({ type: 'USER_CLEAR' });
      } else {
        // Still signed in: let them back into the app
        dispatch({ type: 'SIGNING_OUT', payload: false });
      }
      return;
    }
    queryClient.removeQueries();
  }, [dispatch, queryClient, state.tokenError]);

  /**
   * Back to the Cognito Hosted UI, remembering the current page so the user
   * lands on it again after signing in. Rejects if the redirect can't start.
   */
  const signInAgain = React.useCallback(async () => {
    try {
      await signIn({ window });
    } catch (error) {
      captureError({ data: { error } });
      throw error;
    }
  }, []);

  return { signOut, signInAgain };
}
